import { useCallback } from 'react';
import { useUIStore } from '../../../../store/useUIStore';
import { useModelStore } from '../../../../store/useModelStore';

/**
 * Enhanced Node Operations Hook
 * Handles right-click and double-tap interactions for nodes
 * Provides context menu with "Truss on Node" and "Move Node" options
 */
export const useEnhancedNodeOperations = () => {
  const uiStore = useUIStore();
  const modelStore = useModelStore();

  // Handle enhanced node right click (or double-tap on touchpad)
  const handleEnhancedNodeRightClick = useCallback((nodeId: string, event?: any) => {
    //console.log('🚀 Enhanced node right-clicked:', nodeId);

    // Prevent default browser context menu
    if (event) {
      event.stopPropagation();
      if (event.preventDefault) {
        event.preventDefault();
      }
      if (event.nativeEvent && typeof event.nativeEvent.preventDefault === 'function') {
        event.nativeEvent.preventDefault();
      }
    }

    const node = modelStore?.nodes?.find((n: any) => n.id === nodeId);
    if (!node) {
      console.warn('Node not found:', nodeId);
      return;
    }

    // Calculate screen position for context menu
    let x = 0, y = 0;
    try {
      const canvas = document.querySelector('canvas');
      if (canvas && event?.clientX != null && event?.clientY != null) {
        x = event.clientX;
        y = event.clientY;
      } else if (event?.nativeEvent?.clientX) {
        x = event.nativeEvent.clientX;
        y = event.nativeEvent.clientY;
      } else if (canvas) {
        // Fallback to center of canvas
        const rect = canvas.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }
    } catch (e) {
      console.warn('Error calculating context menu position:', e);
      // Fallback position
      x = 100;
      y = 100;
    }

    //console.log('🎯 Opening enhanced context menu at:', { x, y });
    
    // Open the enhanced node context menu
    uiStore.openEnhancedNodeContextMenu(nodeId, { x, y });
  }, [modelStore, uiStore]);

  // Note: Removed double-click handler - context menu should only open on right-click

  // Handle "Truss on Node" action - opens existing waterfall-style truss config panel
  const handleTrussOnNode = useCallback((nodeId: string) => {
    //console.log('🔧 Opening truss config panel for node:', nodeId);
    
    // Calculate position for truss config panel (offset from context menu)
    const contextMenuPosition = uiStore.enhancedNodeContextMenuPosition;
    const panelPosition = {
      x: contextMenuPosition.x + 50, // Offset to the right
      y: contextMenuPosition.y - 50  // Offset up slightly
    };

    // Open the existing truss configuration panel
    uiStore.openTrussConfigPanel(nodeId, panelPosition);
  }, [uiStore]);

  // Handle "Move Node" action - opens coordinate input dialog
  const handleMoveNode = useCallback((nodeId: string) => {
    //console.log('📐 Opening move node dialog for node:', nodeId);
    
    // Open the node movement dialog (will use existing coordinate input modal)
    uiStore.openNodeMoveDialog(nodeId);
  }, [uiStore]);

  // Close all enhanced node menus and dialogs
  const closeAllEnhancedNodeInteractions = useCallback(() => {
    uiStore.closeEnhancedNodeContextMenu();
    uiStore.closeTrussConfigPanel();
    uiStore.closeNodeMoveDialog();
  }, [uiStore]);

  return {
    handleEnhancedNodeRightClick,
    handleTrussOnNode,
    handleMoveNode,
    closeAllEnhancedNodeInteractions
  };
};

export default useEnhancedNodeOperations;
